<template>
  <div>
    <PageAllow controller="Payment" action="getPaySettings" />
    <Breadcrumbs :items="items" />
    <v-sheet class="transparent-background-color">
      <v-card class="page-card">
        <v-card-title class="mx-3">
          {{ $t("Payment.wsPay.title") }}
          <v-spacer></v-spacer>
        </v-card-title>
        <v-progress-linear :indeterminate="loading"></v-progress-linear>
        <v-card-text>
          <ValidationObserver ref="observer">
            <v-container>
              <v-row class="mt-5">
                <v-col cols="3" class="font-weight-bold text-right">
                  {{ $t("Payment.wsPay.wspay_url") + $t("Global.colon") }}
                  <span class="red--text">*</span>
                </v-col>
                <v-col cols="9" md="7">
                  <ValidationProvider
                    v-slot="{ errors }"
                    :name="$t('Payment.wsPay.wspay_url')"
                    rules="required"
                  >
                    <v-textarea
                      v-model="settings.wspay_url"
                      outlined
                      auto-grow
                      :error-messages="
                        $resparse
                          .getErrorMsgs(serverErrors, 'wspay_url')
                          .concat(errors)
                      "
                    >
                    </v-textarea>
                  </ValidationProvider>
                </v-col>
              </v-row>
              <v-row class="mt-3">
                <v-col cols="3" class="font-weight-bold text-right">
                  {{
                    $t("Payment.wsPay.wspay_privateKey") + $t("Global.colon")
                  }}
                </v-col>
                <v-col cols="9" md="7">
                  <v-file-input
                    v-model="File"
                    show-size
                    :label="$t('Payment.wsPay.wspay_privateKey_upload')"
                    accept=".pem"
                    :error-messages="
                      $resparse.getErrorMsgs(serverErrors, 'wspay_privateKey')
                    "
                  ></v-file-input>
                  <v-text-field
                    v-model="settings.wspay_privateKey"
                    single-line
                    disabled
                  >
                  </v-text-field>
                </v-col>
              </v-row>
              <v-row align="center">
                <v-col cols="3" class="font-weight-bold text-right">
                  {{ $t("Payment.wsPay.wspay_IsvOrgId") + $t("Global.colon") }}
                  <span class="red--text">*</span>
                </v-col>
                <v-col cols="9" md="7">
                  <ValidationProvider
                    v-slot="{ errors }"
                    :name="$t('Payment.wsPay.wspay_IsvOrgId')"
                    rules="required"
                  >
                    <v-text-field
                      v-model="settings.wspay_IsvOrgId"
                      single-line
                      :error-messages="
                        $resparse
                          .getErrorMsgs(serverErrors, 'wspay_IsvOrgId')
                          .concat(errors)
                      "
                    >
                    </v-text-field>
                  </ValidationProvider>
                </v-col>
              </v-row>
              <v-row align="center">
                <v-col cols="3" class="font-weight-bold text-right">
                  {{ $t("Payment.wsPay.wspay_appId") + $t("Global.colon") }}
                  <span class="red--text">*</span>
                </v-col>
                <v-col cols="9" md="7">
                  <ValidationProvider
                    v-slot="{ errors }"
                    :name="$t('Payment.wsPay.wspay_appId')"
                    rules="required"
                  >
                    <v-text-field
                      v-model="settings.wspay_appId"
                      single-line
                      :error-messages="
                        $resparse
                          .getErrorMsgs(serverErrors, 'wspay_appId')
                          .concat(errors)
                      "
                    >
                    </v-text-field>
                  </ValidationProvider>
                </v-col>
              </v-row>
              <v-row align="center">
                <v-col cols="3" class="font-weight-bold text-right">
                  {{ $t("Payment.wsPay.wspay_PayeeId") + $t("Global.colon") }}
                  <span class="red--text">*</span>
                </v-col>
                <v-col cols="9" md="7">
                  <ValidationProvider
                    v-slot="{ errors }"
                    :name="$t('Payment.wsPay.wspay_PayeeId')"
                    rules="required"
                  >
                    <v-text-field
                      v-model="settings.wspay_PayeeId"
                      single-line
                      :error-messages="
                        $resparse
                          .getErrorMsgs(serverErrors, 'wspay_PayeeId')
                          .concat(errors)
                      "
                    >
                    </v-text-field>
                  </ValidationProvider>
                </v-col>
              </v-row>

              <v-row align="center">
                <v-col cols="3" class="font-weight-bold text-right">
                  {{ $t("Payment.wsPay.wspay_enable") + $t("Global.colon") }}
                </v-col>
                <v-col cols="9" md="7">
                  <v-radio-group row v-model="settings.wspay_enable">
                    <v-radio :label="$t('Payment.off')" key="0" value="0" />
                    <v-radio :label="$t('Payment.on')" key="1" value="1" />
                  </v-radio-group>
                </v-col>
              </v-row>
            </v-container>
          </ValidationObserver>
        </v-card-text>
      </v-card>
      <v-card class="page-opts">
        <v-card-title>
          <v-row dense>
            <v-col cols="12">
              <v-btn
                color="success"
                block
                @click="settings.wspay_enable ? (alertDialog = true) : save()"
              >
                <v-icon small class="pe-1">fa-save</v-icon>
                {{ $t("Payment.save") }}
              </v-btn>
            </v-col>
          </v-row>
        </v-card-title>
      </v-card>
    </v-sheet>

    <DialogAlert
      v-model="alertDialog"
      :message="$t('Payment.change_wechat_pay', [$t('Payment.newlandpay')])"
      useConfirm
      @confirm="save()"
    />
  </div>
</template>

<script>
import Payment from "@/api/payment";
import i18n from "@/i18n";
import PageAllow from "@/components/PageAllow";
import Breadcrumbs from "@/components/Breadcrumbs";
import DialogAlert from "@/components/plugins/DialogAlert";
import { mapState } from "vuex";
import { ValidationObserver, ValidationProvider } from "vee-validate";

export default {
  data: () => ({
    settings: {},
    oldPrivateKey: "",
    serverErrors: [],
    File: null,
    alertDialog: false,
  }),

  components: {
    PageAllow,
    Breadcrumbs,
    DialogAlert,
    ValidationObserver,
    ValidationProvider,
  },

  computed: {
    items: () => [
      {
        text: i18n.t("Nav.home"),
        disabled: false,
        href: "/home",
      },
      {
        text: i18n.t("Nav.fundManagement"),
        disabled: false,
        href: "/receipts",
      },
      {
        text: i18n.t("Nav.payment"),
        disabled: true,
        href: "/payment",
      },
    ],
    ...mapState(["loading"]),
  },

  mounted() {
    this.getAlipayFromAPI();
  },

  methods: {
    getAlipayFromAPI() {
      Payment.fetch({
        keys: [
          "wspay_url",
          "wspay_privateKey",
          "wspay_IsvOrgId",
          "wspay_appId",
          "wspay_PayeeId",
          "wspay_enable",
        ],
        parse: 0,
      }).then(
        function (response) {
          if (response.data.status.isOK()) {
            this.settings = response.data.settings;
            this.oldPrivateKey = this.settings.wspay_privateKey;
            if (
              this.settings.wspay_enable == null ||
              typeof this.settings.wspay_enable == "undefined" ||
              this.settings.wspay_enable == undefined
            ) {
              this.settings.wspay_enable = "0";
            }
          }
        }.bind(this)
      );
    },

    save() {
      this.$refs.observer.validate().then(
        function (result) {
          if (result) {
            Payment.WsSave(this.settings).then(
              function (response) {
                if (response.data.status.isOK()) {
                  this.$router.push("/payment");
                }
              }.bind(this)
            );
          }
        }.bind(this)
      );
    },
  },

  watch: {
    File: {
      handler() {
        if (this.File) {
          Payment.WsFileUpload(this.File).then(
            function (response) {
              if (response.data.status.isOK()) {
                this.settings.wspay_privateKey = response.data.wsbank_file_url;
              }
            }.bind(this)
          );
        } else {
          this.settings.wspay_privateKey = this.oldPrivateKey;
        }
      },
    },
  },
};
</script>